import { API_URLS } from '../constants/apiUrls';
import { signalRateLimit } from '../utils/rateLimitSignal';
import type { TokenValidationSuccess, TokenValidationFailure } from '../types/api.types';

/**
 * POST /api/token/validate  { token }
 * Token is sent in the request body — never in the URL — to prevent exposure
 * in server-side access logs and Referer headers.
 * Raw HTTP call — no business logic. Returns response or throws with error code.
 */
export async function validateTokenRequest(
  token: string
): Promise<TokenValidationSuccess> {
  const response = await fetch(API_URLS.validateToken, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache'
    },
    body: JSON.stringify({ token }),
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    const errorCode = (body as { error?: string }).error ?? 'RATE_LIMIT_EXCEEDED';
    const retryAfter = parseInt(response.headers.get('Retry-After') ?? '600', 10);
    signalRateLimit(errorCode, retryAfter);
    throw new Error(errorCode);
  }

  const body = await response.json();

  if (!response.ok) {
    const errBody = body as TokenValidationFailure;
    throw new Error(errBody.error ?? 'UNKNOWN_ERROR');
  }

  return body as TokenValidationSuccess;
}
